import type { DefaultError, UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { useApiClient } from '@/clients';
import { queryKeys } from '@/lib/query-keys';
import { RecommendationStudentDetailsError } from '@/types/errors.types';
import { getCoursePlannerPath } from '@/utils/api-paths';
import { getDistrictId, getHighschoolId } from '@/utils/cp-context';

/** A course recommended to be added to a student's plan. */
export interface RecommendedCourse {
  courseId: string;
  courseName: string;
  /** ISO date the course was last added to a student's plan via a recommendation. */
  lastAddedAt?: string;
}

/** Paging and search values accepted by the recommendations endpoint. */
export interface RecommendationFilters {
  page: number;
  pageSize: number;
  search?: string;
}

/** Typed page returned by the recommendations endpoint. */
export interface RecommendedCoursesResponse {
  data: RecommendedCourse[];
  total: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

/** A student available to receive a course recommendation. */
export interface RecommendationStudent {
  id: string;
  firstName: string;
  lastName: string;
  studentId: string;
  classYear: number;
}

/** Paging and optional class-year and search filter values accepted by the students endpoint. */
export interface RecommendationStudentFilters {
  page: number;
  pageSize?: number;
  classYear?: number;
  search?: string;
}

/** Typed student page returned by the staff students endpoint. */
export interface RecommendationStudentsResponse {
  data: RecommendationStudent[];
  total: number;
}

function parseRecommendationStudent(value: unknown): RecommendationStudent | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const student = value as Record<string, unknown>;
  const rawId = student.id ?? student.nid ?? student.studentId;
  const rawStudentId =
    student.highSchoolStudentId ?? student.studentId ?? student.id ?? student.nid;
  const classYear = Number(student.classYear ?? student.graduationYear);
  if (
    (typeof rawId !== 'string' && typeof rawId !== 'number') ||
    (typeof rawStudentId !== 'string' && typeof rawStudentId !== 'number') ||
    typeof student.firstName !== 'string' ||
    typeof student.lastName !== 'string' ||
    !Number.isInteger(classYear)
  ) {
    return null;
  }
  return {
    id: String(rawId),
    studentId: String(rawStudentId),
    firstName: student.firstName,
    lastName: student.lastName,
    classYear,
  };
}

export function parseRecommendationStudentsResponse(
  value: unknown
): RecommendationStudentsResponse {
  if (typeof value !== 'object' || value === null) {
    throw new Error('Invalid students response');
  }
  const payload = value as Record<string, unknown>;
  const rawStudents = Array.isArray(payload.data)
    ? payload.data
    : Array.isArray(payload.students)
      ? payload.students
      : [];
  const data = rawStudents
    .map(parseRecommendationStudent)
    .filter((student): student is RecommendationStudent => student !== null);
  return {
    data,
    total:
      typeof payload.total === 'number'
        ? payload.total
        : typeof payload.totalItems === 'number'
          ? payload.totalItems
          : data.length,
  };
}

function parseRecommendedCourse(value: unknown): RecommendedCourse | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const item = value as Record<string, unknown>;
  if (typeof item.courseId !== 'string' || typeof item.courseName !== 'string') {
    return null;
  }
  return {
    courseId: item.courseId,
    courseName: item.courseName,
    lastAddedAt: typeof item.lastAddedAt === 'string' ? item.lastAddedAt : undefined,
  };
}

export function parseRecommendedCoursesResponse(value: unknown): RecommendedCoursesResponse {
  if (typeof value !== 'object' || value === null) {
    throw new Error('Invalid recommendations response');
  }
  const payload = value as Record<string, unknown>;
  if (!Array.isArray(payload.data)) {
    throw new Error('Invalid recommendations response');
  }
  const data = payload.data
    .map(parseRecommendedCourse)
    .filter((course): course is RecommendedCourse => course !== null);
  const total = typeof payload.total === 'number' ? payload.total : data.length;
  const pageSize = typeof payload.pageSize === 'number' ? payload.pageSize : data.length;
  return {
    data,
    total,
    pageSize,
    currentPage: typeof payload.currentPage === 'number' ? payload.currentPage : 1,
    totalPages:
      typeof payload.totalPages === 'number'
        ? payload.totalPages
        : Math.max(1, Math.ceil(total / pageSize)),
  };
}

/** Fetches recommended courses for the current school context. */
export function useGetRecommendedCourses(
  filters: RecommendationFilters
): UseQueryResult<RecommendedCoursesResponse, DefaultError> {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.recommendations.list(filters),
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<RecommendedCoursesResponse> => {
      const response = await apiClient.get<unknown>(
        getCoursePlannerPath('recommendations', 'find'),
        {
          params: {
            page: filters.page,
            pageSize: filters.pageSize,
            ...(filters.search ? { search: filters.search.trim().toLowerCase() } : {}),
          },
        }
      );
      return parseRecommendedCoursesResponse(response.data);
    },
  });
}
/** Fetches students available for a course recommendation. */
export function useGetRecommendationStudents(
  filters: RecommendationStudentFilters,
  enabled = true
): UseQueryResult<RecommendationStudentsResponse, DefaultError> {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.recommendations.students(filters),
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<RecommendationStudentsResponse> => {
      const response = await apiClient.get<unknown>('/students', {
        params: {
          page: filters.page,
          ...(filters.pageSize ? { pageSize: filters.pageSize } : {}),
          ...(filters.classYear
            ? { classYears: JSON.stringify({ start: filters.classYear, end: filters.classYear }) }
            : {}),
          ...(filters.search ? { search: filters.search.trim() } : {}),
        },
      });
      return parseRecommendationStudentsResponse(response.data);
    },
  });
}

const RECOMMENDED_STUDENTS_PAGE_SIZE = 100;
const recommendedStudentPageSchema = z.object({
  data: z.array(
    z.object({
      id: z.union([z.string(), z.number()]),
      firstName: z.string(),
      lastName: z.string(),
      highSchoolStudentId: z.union([z.string(), z.number()]).nullish(),
      studentId: z.union([z.string(), z.number()]).nullish(),
      classYear: z.coerce.number().int().positive(),
    })
  ),
  totalPages: z.number().int().nonnegative(),
});

/**
 * Loads saved recommendation students across grades, including every roster result page.
 *
 * @param studentIds - Naviance IDs saved on the recommendation, independent of the grade filter
 * @returns A query containing validated student names, school IDs, and class years
 */
export function useGetRecommendedStudentDetails(
  studentIds: string[]
): UseQueryResult<RecommendationStudent[], DefaultError> {
  const apiClient = useApiClient();
  const highschoolId = getHighschoolId();
  const districtId = getDistrictId();

  return useQuery({
    queryKey: queryKeys.recommendations.students({ studentIds, highschoolId, districtId }),
    enabled: studentIds.length > 0,
    queryFn: async (): Promise<RecommendationStudent[]> => {
      if (!highschoolId && !districtId) {
        throw new RecommendationStudentDetailsError('missing-context');
      }

      const requestedIds = new Set(studentIds);
      const students: RecommendationStudent[] = [];
      let totalPages = 1;
      for (let page = 1; page <= totalPages; page += 1) {
        // Do not constrain the roster by the available-panel grade or search filters.
        const response = await apiClient.get<unknown>('/students', {
          params: { page, limit: RECOMMENDED_STUDENTS_PAGE_SIZE },
        });
        const parsedPage = recommendedStudentPageSchema.parse(response.data);
        totalPages = parsedPage.totalPages;
        for (const student of parseRecommendationStudentsResponse(parsedPage).data) {
          if (requestedIds.delete(student.id)) {
            students.push(student);
          }
        }
        if (requestedIds.size === 0) {
          break;
        }
      }
      if (requestedIds.size > 0) {
        throw new RecommendationStudentDetailsError('missing-students');
      }
      return students;
    },
  });
}

/**
 * Deletes a course recommendation and all of its student associations (handled server-side,
 * across every class year). Invalidates the recommendations list on success.
 */
export function useDeleteRecommendation(): UseMutationResult<string, DefaultError, string> {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (courseId: string) => {
      await apiClient.delete(
        `${getCoursePlannerPath('recommendations')}/${encodeURIComponent(courseId)}`
      );
      return courseId;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.recommendations.all });
    },
  });
}

/** A student currently recommended for a course. */
export interface CourseRecommendationStudentItem {
  id: string;
  classYear?: number;
}

/** Details of current recommendations for a course. */
export interface CourseRecommendationDetail {
  courseId: string;
  courseName?: string;
  students: CourseRecommendationStudentItem[];
}

/** Parses the API response for recommendations on a specific course. */
export function parseCourseRecommendationResponse(value: unknown): CourseRecommendationDetail {
  if (typeof value !== 'object' || value === null) {
    throw new Error('Invalid course recommendation response');
  }
  const payload = value as Record<string, unknown>;
  const courseId = typeof payload.courseId === 'string' ? payload.courseId : '';
  const courseName = typeof payload.courseName === 'string' ? payload.courseName : undefined;
  const studentsRaw = Array.isArray(payload.students) ? payload.students : [];
  const students: CourseRecommendationStudentItem[] = studentsRaw
    .filter((s): s is Record<string, unknown> => typeof s === 'object' && s !== null)
    .map((s) => ({
      id: String(s.id ?? ''),
      classYear:
        typeof s.classYear === 'number'
          ? s.classYear
          : typeof s.classYear === 'string' && !Number.isNaN(Number(s.classYear))
            ? Number(s.classYear)
            : undefined,
    }))
    .filter((s) => Boolean(s.id));

  return {
    courseId,
    courseName,
    students,
  };
}

/** Fetches existing recommendations for a specific course (used in Edit mode). */
export function useGetRecommendationsByCourse(
  courseId: string,
  enabled = true
): UseQueryResult<CourseRecommendationDetail, DefaultError> {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.recommendations.byCourse(courseId),
    enabled: Boolean(courseId) && enabled,
    queryFn: async (): Promise<CourseRecommendationDetail> => {
      const response = await apiClient.get<unknown>(
        getCoursePlannerPath('recommendations', courseId)
      );
      return parseCourseRecommendationResponse(response.data);
    },
  });
}

/** Payload for saving recommendations (creations and deletions). */
export interface SaveRecommendationPayload {
  courseId: string;
  putRequest: string[];
  deleteRequest: string[];
}

/** Mutation hook to create or update course recommendations. */
export function useSaveRecommendation(): UseMutationResult<
  void,
  DefaultError,
  SaveRecommendationPayload
> {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: SaveRecommendationPayload): Promise<void> => {
      await apiClient.post(getCoursePlannerPath('recommendations'), payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.recommendations.all });
    },
  });
}
